import type { OfferLine } from "@/hooks/use-offers";
import { readOvertimeSplit } from "@/lib/offer-calc";
import { PHASE_ORDER } from "@/lib/offer-budget-image-geometry";

export interface BudgetPageSlice {
  lines: OfferLine[];
  pageNumber: number;
  pageTotal: number;
  showTotals: boolean;
}

/**
 * Høydeestimat for fase-headers + linjer + overtid-rader. Brukes av
 * både PNG- og Keynote-eksport når vi avgjør om budsjettet må pagineres.
 */
export function estimateBudgetLinesHeight(
  visibleLines: OfferLine[],
  rowH: number,
  phaseH: number,
  phasesToInclude: typeof PHASE_ORDER = PHASE_ORDER,
): number {
  let h = 0;
  for (const phase of phasesToInclude) {
    const phaseLines = visibleLines.filter((l) => l.phase === phase.key);
    if (phaseLines.length === 0) continue;
    h += phaseH + phaseLines.length * rowH;
    for (const l of phaseLines) {
      if (readOvertimeSplit({ overtime_split: l.overtime_split ?? null })) {
        h += rowH;
      }
    }
  }
  return h;
}

/**
 * Deler linjer på sider uten å splitte en fase-header fra dens første
 * linje. En fase som selv er høyere enn maxHeight fortsetter på neste
 * side med ny header.
 */
export function paginateBudgetLines(
  visibleLines: OfferLine[],
  rowH: number,
  phaseH: number,
  maxHeight: number,
): OfferLine[][] {
  const pages: OfferLine[][] = [];
  let current: OfferLine[] = [];
  let currentH = 0;

  for (const phase of PHASE_ORDER) {
    const phaseLines = visibleLines.filter((l) => l.phase === phase.key);
    if (phaseLines.length === 0) continue;

    let phaseHeaderUsed = false;
    for (const line of phaseLines) {
      const headerCost = phaseHeaderUsed ? 0 : phaseH;
      const hasOvertime = !!readOvertimeSplit({
        overtime_split: line.overtime_split ?? null,
      });
      const lineCost = rowH + (hasOvertime ? rowH : 0);
      if (currentH + headerCost + lineCost > maxHeight && current.length > 0) {
        pages.push(current);
        current = [];
        currentH = 0;
        phaseHeaderUsed = false;
      }
      if (!phaseHeaderUsed) {
        currentH += phaseH;
        phaseHeaderUsed = true;
      }
      current.push(line);
      currentH += lineCost;
    }
  }
  if (current.length > 0) pages.push(current);
  if (pages.length === 0) pages.push([]);
  return pages;
}

/** PNG 16:9: plass til linjer i det grå feltet etter padding + SUM-rad. */
export const PNG_LINES_MAX_PX = 500;

/**
 * Keynote/PPTX: tabellbredden.
 *
 * Malens egen er 7,73" av dens 26,67"-lerret → 3,865" hos oss (VIB-2556).
 * Den er for knapp i praksis: et sjufigret totalbeløp krever 0,69" i
 * beløpskolonnen, mot malens 0,56", så tabellen måtte tære på
 * beskrivelseskolonnen ved hvert eneste store tilbud.
 *
 * Vi bruker 4,30" i stedet, sentrert i det grå feltet. Malen lot 1,45" stå
 * tom mellom panelet og tabellen og 1,30" til høyre; her blir det 1,16" på
 * hver side. Tabellen blir 11 % bredere, marginene jevne, og et sjufigret
 * beløp får plass uten å ta noe fra beskrivelsen.
 */
export const PPTX_COMPACT_TABLE_IN = 4.3;

/**
 * Sikkerhetsmargin på den målte tekstbredden i beløpskolonnen.
 *
 * `helveticaWidthIn` regner på Helvetica sine metrikker, men SUM-raden settes
 * i Helvetica Neue Medium, og Keynote og PowerPoint runder av tekstbredder
 * litt ulikt. 8 % dekker forskjellen med god margin. Feiler anslaget for lavt
 * brekkes tallet over to linjer, og det er verre enn noen hundredels tomme
 * ekstra i en kolonne.
 */
export const PPTX_AMOUNT_SAFETY = 1.08;

/**
 * Cellemargin rundt teksten i PPTX-tabellen, i tommer — det er enheten
 * pptxgenjs legger rett i `marT`/`marB` her. Malen bruker 0,0278" på alle
 * fire sider. Vi hadde 0,04" topp/bunn, som sammen med malens radhøyde ga
 * mindre plass enn fonten trenger (0,87 × fontstørrelsen mot malens 1,10),
 * og da klipper PowerPoint og Keynote bunnen av hver linje.
 */
export const PPTX_CELL_MARGIN_IN = 0.0278;

/**
 * PNG-radmål. VIB-2556: malens rad er 0,178" ≈ 17 px på 1280-lerretet,
 * mot våre tidligere ~21 px. Faktorene følger `LINE_HEIGHT` + padding i
 * `PhaseTable`, så estimatet ikke drifter fra det som faktisk rendres.
 */
export function pngRowMetrics(fontSize: number): {
  row: number;
  phase: number;
  max: number;
} {
  return {
    row: Math.max(13, Math.round(fontSize * 1.75)),
    phase: Math.max(16, Math.round(fontSize * 2.05)),
    max: PNG_LINES_MAX_PX,
  };
}

/**
 * Malens kolonnefordeling i budsjett-tabellen: 3,91 / 0,96 / 1,32 / 0,44 /
 * 1,11 av dens 7,73"-brede tabell (VIB-2556).
 */
export const PPTX_COL_FRACTIONS = [0.506, 0.124, 0.171, 0.057, 0.144] as const;

/**
 * Bredde i tommer for en streng satt i Helvetica ved gitt punktstørrelse.
 *
 * Helvetica har faste metrikker, så dette trenger ingen måling i nettleseren:
 * sifre og valutategn er 0,556 em og mellomrom 0,278 em (formatNOK bruker hardt
 * mellomrom, som er like bredt). Alt annet estimeres til 0,60 em, i overkant av
 * en typisk gemen, slik at anslaget aldri blir for lavt. Bold legger på 3 %.
 *
 * Brukes til å sikre at beløpskolonnen faktisk rommer tallet: pptxgenjs har
 * ingen «shrink to fit», så et beløp som ikke får plass brekkes over to linjer
 * inne i cellen i stedet for å krympe.
 */
export function helveticaWidthIn(
  text: string,
  fontSizePt: number,
  bold = false,
): number {
  let em = 0;
  for (const ch of text) {
    if (ch >= "0" && ch <= "9") em += 0.556;
    else if (ch === " " || ch === " " || ch === " ") em += 0.278;
    else em += 0.6;
  }
  return ((em * fontSizePt) / 72) * (bold ? 1.03 : 1);
}

/**
 * Kolonnebredder for PPTX-tabellen, i tommer.
 *
 * Utgangspunktet er malens fordeling. Er det bredeste beløpet for bredt for
 * malens beløpskolonne — typisk SUM-raden, som settes større og i bold —
 * utvides beløpskolonnen akkurat nok, og bredden tas fra beskrivelseskolonnen.
 * Den har klart mest slakk og brekker uansett pent over to linjer etter
 * VIB-2557, mens et brukket tall («401 29 / 3») alltid er feil.
 *
 * Beskrivelseskolonnen får aldri under 35 % av tabellen. Treffer vi det gulvet
 * er tabellen for smal for tallene i seg selv, og da er riktig svar en mindre
 * font, ikke en ubrukelig beskrivelseskolonne.
 */
export function pptxColumnWidths(
  tableWidth: number,
  amountCells: Array<{ text: string; fontSize: number; bold?: boolean }>,
): number[] {
  // Malens egne mål er avrundet til to desimaler og summerer til 1,002, så
  // de normaliseres — ellers blir tabellen 0,2 % bredere enn `tableWidth`,
  // som er malens mål og det pagineringen regner med.
  const sumFractions = PPTX_COL_FRACTIONS.reduce((a, b) => a + b, 0);
  const cols = PPTX_COL_FRACTIONS.map((f) => (tableWidth * f) / sumFractions);
  const needed = amountCells.reduce(
    (max, c) =>
      Math.max(
        max,
        helveticaWidthIn(c.text, c.fontSize, c.bold) * PPTX_AMOUNT_SAFETY +
          PPTX_CELL_MARGIN_IN * 2,
      ),
    0,
  );
  const deficit = needed - cols[4];
  if (deficit <= 0) return cols;

  const descFloor = tableWidth * 0.35;
  const give = Math.min(deficit, cols[0] - descFloor);
  if (give <= 0) return cols;

  cols[0] -= give;
  cols[4] += give;
  return cols;
}
